import React, { useEffect, useState } from "react";
import Breadcrumbs from "../common/Breadcrumbs";
import AOS from "aos";
import "aos/dist/aos.css";
import { useSelector } from "react-redux";
import { ContactUs } from "../../core/data/interface/interface";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const Contact = () => {
  const data = useSelector((state: any) => state.contactdata);

  // Form State
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    comments: "",
  });
  
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear error for field when user types
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: "" });
    }
  };

  const validate = () => {
    const newErrors: any = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.comments.trim()) newErrors.comments = "Message is required";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setStatusMessage(null);

    try {
      // Simulate an API call delay for the contact form submission
      await new Promise((resolve) => setTimeout(resolve, 2000));
      
      // Assume success since we are simulating the endpoint for now
      setStatusMessage({ type: 'success', text: 'Thank you for reaching out! We have received your message and will get back to you shortly.' });
      setFormData({ name: "", email: "", phone: "", comments: "" });
    } catch (error) {
      setStatusMessage({ type: 'error', text: 'An error occurred while sending your message. Please try again later.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Eleego Luxury Rentals</title>
        <meta name="description" content="Get in touch with Eleego for inquiries, custom bookings, and premium chauffeur services in Switzerland." />
      </Helmet>
      
      <div className="main-wrapper lux-detail-page" style={{ paddingBottom: '100px' }}>
        <Breadcrumbs title="Contact Us" subtitle="Contact" maintitle="" />
        
        {/* ── Hero title band ── */}
        <section className="lux-hero" style={{ minHeight: "35vh", background: "url('assets/img/slider/slider-3.jpg') center/cover no-repeat" }}>
          <div className="lux-hero-overlay" style={{ background: "linear-gradient(to right, rgba(10,10,10,0.95) 0%, rgba(10,10,10,0.7) 100%)", position: 'absolute', inset: 0 }}></div>
          <div className="container position-relative h-100 d-flex flex-column justify-content-center" style={{ paddingTop: '100px' }}>
            <div className="row">
              <div className="col-lg-8" data-aos="fade-right">
                <p className="lux-eyebrow">Get In Touch</p>
                <h1 className="text-white lux-hero-heading" style={{ fontSize: "3.5rem" }}>
                  We are here to assist you
                </h1>
                <p className="lux-hero-body">
                  Whether you have a special request, need to tailor a custom itinerary, or just want more information about our fleet, our dedicated team is at your service.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="contact-section mt-5">
          <div className="container">
            <div className="row g-5">
              {/* Left Column: Contact Info Cards */}
              <div className="col-lg-5" data-aos="fade-up" data-aos-duration={1200}>
                <div className="row g-4">
                  {data.map((info: ContactUs, index: number) => (
                    <div key={index} className="col-12">
                      <div className="lux-card d-flex align-items-center p-4" style={{ borderRadius: "12px", border: "1px solid rgba(200, 164, 93, 0.2)", background: "rgba(255, 255, 255, 0.03)", backdropFilter: "blur(10px)" }}>
                        <div className="icon-wrapper me-4" style={{ width: "60px", height: "60px", borderRadius: "50%", background: "rgba(200, 164, 93, 0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--luxury-gold)", fontSize: "24px" }}>
                          <i className={info.icon} />
                        </div>
                        <div>
                          <h4 className="text-white mb-2" style={{ fontWeight: 600, fontSize: "1.1rem" }}>{info.title}</h4>
                          {info.type === "phone" ? (
                            <Link to={info.link} className="text-muted" style={{ textDecoration: 'none', transition: 'color 0.3s' }}>
                              {info.text}
                            </Link>
                          ) : (
                            <p className="mb-0">
                              <Link to={info.link} className="text-muted" style={{ textDecoration: 'none', transition: 'color 0.3s' }}>
                                {info.text}
                              </Link>
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column: Form */}
              <div className="col-lg-7" data-aos="fade-left" data-aos-duration={1200}>
                <div className="lux-card p-5" style={{ borderRadius: "16px", border: "1px solid rgba(255, 255, 255, 0.08)", background: "#111", boxShadow: "0 20px 40px rgba(0,0,0,0.5)" }}>
                  <div className="mb-5">
                    <h2 className="text-white mb-3" style={{ fontWeight: 500, fontFamily: "var(--font-heading)" }}>Send a Request</h2>
                    <div style={{ width: "40px", height: "3px", backgroundColor: "var(--luxury-gold)" }} className="mb-3" />
                    <p className="text-muted">Fill out the details below and we will respond as soon as possible.</p>
                  </div>

                  {statusMessage && (
                    <div className={`alert ${statusMessage.type === 'success' ? 'alert-success' : 'alert-danger'} mb-4`} style={{ backgroundColor: statusMessage.type === 'success' ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)', border: `1px solid ${statusMessage.type === 'success' ? 'rgba(40, 167, 69, 0.3)' : 'rgba(220, 53, 69, 0.3)'}`, color: statusMessage.type === 'success' ? '#4CAF50' : '#F44336' }}>
                      {statusMessage.text}
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="row g-4">
                      <div className="col-md-6">
                        <label className="text-white mb-2" style={{ fontSize: "0.9rem" }}>Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                          style={{ backgroundColor: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.1)", color: "#fff", padding: "12px 16px" }}
                          placeholder="Your full name"
                        />
                        {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                      </div>
                      
                      <div className="col-md-6">
                        <label className="text-white mb-2" style={{ fontSize: "0.9rem" }}>Email Address <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                          style={{ backgroundColor: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.1)", color: "#fff", padding: "12px 16px" }}
                          placeholder="Your email"
                        />
                        {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                      </div>
                      
                      <div className="col-md-12">
                        <label className="text-white mb-2" style={{ fontSize: "0.9rem" }}>Phone number <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                          style={{ backgroundColor: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.1)", color: "#fff", padding: "12px 16px" }}
                          placeholder="Your phone number"
                        />
                        {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                      </div>
                      
                      <div className="col-md-12">
                        <label className="text-white mb-2" style={{ fontSize: "0.9rem" }}>Comments <span className="text-danger">*</span></label>
                        <textarea
                          name="comments"
                          value={formData.comments}
                          onChange={handleChange}
                          className={`form-control ${errors.comments ? 'is-invalid' : ''}`}
                          rows={5}
                          style={{ backgroundColor: "rgba(255, 255, 255, 0.05)", border: "1px solid rgba(255, 255, 255, 0.1)", color: "#fff", padding: "12px 16px", resize: "none" }}
                          placeholder="How can we help you?"
                        />
                        {errors.comments && <div className="invalid-feedback">{errors.comments}</div>}
                      </div>
                    </div>
                    
                    <button 
                      type="submit" 
                      className="btn lux-btn-gold w-100 mt-5 d-flex align-items-center justify-content-center"
                      disabled={isSubmitting}
                      style={{ padding: "16px" }}
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Sending Request...
                        </>
                      ) : (
                        "Send Enquiry"
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
